import { useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";

/* ============================================================================
 * Module awareness
 * ----------------------------------------------------------------------------
 * The Accounts screens (Account Master, Ledger, Account Entry, Cost Center …)
 * are shared by three hosts:
 *
 *   • Daily Tick  – plain stores, Accounts sits directly in the Daily Tick
 *                   sidebar.
 *   • Travel ERP  – Accounts is reached from the Travel dashboard's Masters
 *                   dropdown.
 *   • Pawn Shop   – Accounts + Ledger sit in the pawn module's own sidebar.
 *
 * Which host we're in decides two things: where the "home" button goes, and
 * whether cross-links between the accounts screens must stay inside the pawn
 * route tree (so the pawn chrome isn't dropped). Both live here so no screen
 * has to hard-code /travel/dashboard again.
 * ========================================================================== */

// Stores carry is_pawn / is_travel flags (MySQL tinyints, so they can arrive as
// 1, "1", true or null).
export const isPawnStore = (store) => Boolean(Number(store?.is_pawn ?? 0));
export const isTravelStore = (store) => Boolean(Number(store?.is_travel ?? 0));

export const MODULE_HOME = {
  pawn: "/pawn/home",
  travel: "/travel/dashboard",
  dailytick: "/home",
};

/** Human names for the three hosts — used by the store switcher's type labels. */
export const MODULE_LABEL = {
  pawn: "Pawn Shop",
  travel: "Travel ERP",
  dailytick: "Daily Tick",
};

/**
 * The module a store belongs to. A store flagged for both falls back to pawn
 * (the pawn sidebar carries a Daily Tick escape hatch of its own).
 */
export const getStoreModule = (store) => {
  if (isPawnStore(store)) return "pawn";
  if (isTravelStore(store)) return "travel";
  return "dailytick";
};

/**
 * The module name to show for a store, and the route a session scoped to it
 * must land on. Both take any shape that carries is_pawn / is_travel — a
 * current_store_info row or a /switchable-stores row alike.
 *
 * getStoreHome answers one question for every caller that has to place a
 * session: where does this store belong? A store switch uses it, and so does a
 * fresh login — a store that runs a module of its own opens on that module's
 * dashboard rather than on the Daily Tick home it has no use for. A store with
 * no module flags is a Daily Tick store, and /home is genuinely its dashboard.
 */
export const getStoreModuleLabel = (store) =>
  MODULE_LABEL[getStoreModule(store)];
export const getStoreHome = (store) => MODULE_HOME[getStoreModule(store)];

/** Pawn routes are the whole /pawn subtree, including /pawn/accounts*. */
export const isPawnPath = (pathname = "") => pathname.startsWith("/pawn");

/**
 * Resolves the active module: the URL wins when it is unambiguous (we are
 * physically inside /pawn or /travel), otherwise the current store's flags
 * decide — that's the case for the shared /accounts* routes on a Daily Tick
 * store.
 */
export const useAppModule = () => {
  const { pathname } = useLocation();
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};

  const module = isPawnPath(pathname)
    ? "pawn"
    : pathname.startsWith("/travel")
      ? "travel"
      : getStoreModule(currentStoreInfo);

  return {
    module,
    isPawn: module === "pawn",
    isTravel: module === "travel",
    homePath: MODULE_HOME[module],
  };
};

/** Just the home route for the active module. */
export const useModuleHome = () => useAppModule().homePath;

/* ----------------------------------------------------------------------------
 * Accounts navigation
 * --------------------------------------------------------------------------*/

/**
 * Path/navigate helpers for the shared Accounts screens.
 *
 *   path("/accounts-ledger")  →  "/accounts-ledger"        (Daily Tick / Travel)
 *                             →  "/pawn/accounts-ledger"   (inside the pawn module)
 *
 * `goHome` lands on the host module's home screen.
 */
export const useAccountsNav = () => {
  const navigate = useNavigate();
  const { module, isPawn, homePath } = useAppModule();

  const path = useCallback((to) => (isPawn ? `/pawn${to}` : to), [isPawn]);

  const go = useCallback(
    (to, options) => navigate(path(to), options),
    [navigate, path],
  );

  const goHome = useCallback(() => navigate(homePath), [navigate, homePath]);

  return { module, inPawn: isPawn, homePath, path, go, goHome };
};
